import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { Box, InputLabel, OutlinedInput } from "@mui/material";

import Title from "../../../common/Title";
import useAuth from "../../../../hooks/useAuth";
import InputFieldBox from "../../../common/InputFieldBox";
import InputFieldAutoComplete from "../../../common/InputFieldAutoComplete";

const NULLABLE_PLAN_FIELDS = ["message_route_id", "voice_route_id", "package_id"];

const PlanForm = ({ isUpdate, handleSubmit, initialValues, loading }) => {
  const { user } = useAuth();
  const nav = useNavigate();

  const initialData = {
    plan_name: "",
    voice_enabled: false,
    text_enabled: true,
    message_route_id: null,
    voice_route_id: null,
    package_id: null,
    value_unit: null,
    notify_value: null,
    throttle_value: null,
    throttle_speed: null,
    limit_value: null,
  };

  if (!user.admin) {
    nav("/");
  }

  const [formData, setFormData] = useState(null);

  const valueUnitOptions = ["KB", "MB", "GB", "TB"];
  const throttleSpeedOptions = [
    "1-mbps",
    "5-mbps",
    "20-mbps",
    "50-mbps",
    "100-mbps",
    "64-kbps",
    "128-kbps",
    "256-kbps",
    "512-kbps",
  ];

  const createSchema = yup.object({
    plan_name: yup.string("plan_name").required("Plan name is required"),
    voice_enabled: yup.bool("voice_enabled").required(),
    text_enabled: yup.bool("text_enabled").required(),
    message_route_id: yup.string("message_route_id").nullable().optional(),
    voice_route_id: yup.string("voice_route_id").nullable().optional(),
    package_id: yup.string("package_id").nullable().optional(),
    value_unit: yup.string("value_unit").required("Value unit is required"),
    notify_value: yup
      .number("notify_value")
      .required("Notify value is required")
      .min(0, "Must be 0 or greater"),
    throttle_value: yup
      .number("throttle_value")
      .required("Throttle value is required")
      .min(0, "Must be 0 or greater"),
    throttle_speed: yup.string("throttle_speed").required("Throttle speed is required"),
    limit_value: yup
      .number("limit_value")
      .required("Limit value is required")
      .min(0, "Must be 0 or greater"),
  });

  const updateSchema = yup.object({
    plan_name: yup.string("plan_name").required("Plan name is required"),
    voice_enabled: yup.bool("voice_enabled").required(),
    text_enabled: yup.bool("text_enabled").required(),
    message_route_id: yup.string("message_route_id").nullable().optional(),
    voice_route_id: yup.string("voice_route_id").nullable().optional(),
    package_id: yup.string("package_id").nullable().optional(),
    value_unit: yup.string("value_unit").required("Value unit is required"),
    notify_value: yup
      .number("notify_value")
      .required("Notify value is required")
      .min(0, "Must be 0 or greater"),
    throttle_value: yup
      .number("throttle_value")
      .required("Throttle value is required")
      .min(0, "Must be 0 or greater"),
    throttle_speed: yup.string("throttle_speed").required("Throttle speed is required"),
    limit_value: yup
      .number("limit_value")
      .required("Limit value is required")
      .min(0, "Must be 0 or greater"),
  });

  const validationSchema = isUpdate ? updateSchema : createSchema;

  const formik = useFormik({
    initialValues:
      initialValues && Object.keys(initialValues).length
        ? { ...initialValues }
        : { ...initialData },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const requestData = Object.keys(values).reduce((obj, key) => {
        if (!Object.keys(validationSchema.fields).includes(key)) {
          return obj;
        }

        const value = values[key];
        if (
          isUpdate &&
          NULLABLE_PLAN_FIELDS.includes(key) &&
          (value === null || value === undefined || value === "")
        ) {
          obj[key] = null;
          return obj;
        }

        if (value !== null && value !== undefined && value !== "") {
          obj[key] = values[key];
        }
        return obj;
      }, {});

      setFormData(requestData);
    },
    validateOnChange: true,
  });

  useEffect(() => {
    if (formData) {
      handleSubmit(formData);
      if (!loading) {
        formik.setSubmitting(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  if (loading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%", mb: 5 }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content={`${isUpdate ? "Update" : "Create"} Plan`}
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
          maxWidth={350}
        >
          <Grid item xs={12} style={{ width: 350 }}>
            <FormControl
              error={
                formik.touched.plan_name && Boolean(formik.errors.plan_name)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="plan_name">Plan Name</InputLabel>
              <OutlinedInput
                name="plan_name"
                label="Plan Name"
                id="plan_name"
                value={formik.values.plan_name}
                onChange={formik.handleChange}
              />
              {formik.touched.plan_name && Boolean(formik.errors.plan_name) ? (
                <FormHelperText id="plan_name-error-text">
                  {formik.errors.plan_name}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} style={{ width: 350 }}>
            <FormControl
              error={Boolean(formik.errors.voice_enabled) ? true : false}
              sx={{ width: "100%" }}
            >
              <InputFieldBox
                isCheckbox={true}
                value={formik.values.voice_enabled}
                onChange={(event) =>
                  formik.setFieldValue("voice_enabled", event.target.checked)
                }
                label="Voice Enabled"
              />
              {Boolean(formik.errors.voice_enabled) ? (
                <FormHelperText id="voice_enabled-error-text">
                  {formik.errors.voice_enabled}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} style={{ width: 350 }}>
            <FormControl
              error={Boolean(formik.errors.text_enabled) ? true : false}
              sx={{ width: "100%" }}
            >
              <InputFieldBox
                isCheckbox={true}
                value={formik.values.text_enabled}
                onChange={(event) =>
                  formik.setFieldValue("text_enabled", event.target.checked)
                }
                label="Text Enabled"
              />
              {Boolean(formik.errors.text_enabled) ? (
                <FormHelperText id="text_enabled-error-text">
                  {formik.errors.text_enabled}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} style={{ width: 350 }}>
            <FormControl
              error={Boolean(formik.errors.message_route_id) ? true : false}
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="message_route_id">
                Message Route ID
              </InputLabel>
              <OutlinedInput
                name="message_route_id"
                label="Message Route ID"
                id="message_route_id"
                value={formik.values.message_route_id || ""}
                onChange={formik.handleChange}
              />
              {formik.touched.message_route_id &&
              Boolean(formik.errors.message_route_id) ? (
                <FormHelperText id="message_route_id-error-text">
                  {formik.errors.message_route_id}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} style={{ width: 350 }}>
            <FormControl
              error={Boolean(formik.errors.voice_route_id) ? true : false}
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="voice_route_id">Voice Route ID</InputLabel>
              <OutlinedInput
                name="voice_route_id"
                label="Voice Route ID"
                id="voice_route_id"
                value={formik.values.voice_route_id || ""}
                onChange={formik.handleChange}
              />
              {formik.touched.voice_route_id &&
              Boolean(formik.errors.voice_route_id) ? (
                <FormHelperText id="voice_route_id-error-text">
                  {formik.errors.voice_route_id}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} style={{ width: 350 }}>
            <FormControl
              error={Boolean(formik.errors.package_id) ? true : false}
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="package_id">Package ID</InputLabel>
              <OutlinedInput
                name="package_id"
                label="Package ID"
                id="package_id"
                value={formik.values.package_id || ""}
                onChange={formik.handleChange}
              />
              {formik.touched.package_id && Boolean(formik.errors.package_id) ? (
                <FormHelperText id="package_id-error-text">
                  {formik.errors.package_id}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} minWidth={350}>
            <FormControl
              error={
                formik.touched.value_unit &&
                Boolean(formik.errors.value_unit)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputFieldAutoComplete
                isSelect
                options={valueUnitOptions}
                name="value_unit"
                label="Value Unit"
                id="value_unit"
                getOptionLabel={(option) => option}
                value={formik.values.value_unit}
                onChange={(_, val) =>
                  formik.setFieldValue("value_unit", val)
                }
              />
              {formik.touched.value_unit &&
              Boolean(formik.errors.value_unit) ? (
                <FormHelperText id="value_unit-error-text">
                  {formik.errors.value_unit}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} style={{ width: 350 }}>
            <FormControl
              error={
                formik.touched.notify_value &&
                Boolean(formik.errors.notify_value)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="notify_value">Notify Value</InputLabel>
              <OutlinedInput
                name="notify_value"
                label="Notify Value"
                id="notify_value"
                type="number"
                value={
                  formik.values.notify_value !== null
                    ? formik.values.notify_value
                    : ""
                }
                onChange={formik.handleChange}
              />
              {formik.touched.notify_value &&
              Boolean(formik.errors.notify_value) ? (
                <FormHelperText id="notify_value-error-text">
                  {formik.errors.notify_value}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} style={{ width: 350 }}>
            <FormControl
              error={
                formik.touched.throttle_value &&
                Boolean(formik.errors.throttle_value)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="throttle_value">
                Throttle Value
              </InputLabel>
              <OutlinedInput
                name="throttle_value"
                label="Throttle Value"
                id="throttle_value"
                type="number"
                value={
                  formik.values.throttle_value !== null
                    ? formik.values.throttle_value
                    : ""
                }
                onChange={formik.handleChange}
              />
              {formik.touched.throttle_value &&
              Boolean(formik.errors.throttle_value) ? (
                <FormHelperText id="throttle_value-error-text">
                  {formik.errors.throttle_value}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} minWidth={350}>
            <FormControl
              error={
                formik.touched.throttle_speed &&
                Boolean(formik.errors.throttle_speed)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputFieldAutoComplete
                isSelect
                options={throttleSpeedOptions}
                name="throttle_speed"
                label="Throttle Speed"
                id="throttle_speed"
                getOptionLabel={(option) => option}
                value={formik.values.throttle_speed}
                onChange={(_, val) =>
                  formik.setFieldValue("throttle_speed", val)
                }
              />
              {formik.touched.throttle_speed &&
              Boolean(formik.errors.throttle_speed) ? (
                <FormHelperText id="throttle_speed-error-text">
                  {formik.errors.throttle_speed}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} style={{ width: 350 }}>
            <FormControl
              error={
                formik.touched.limit_value &&
                Boolean(formik.errors.limit_value)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="limit_value">Limit Value</InputLabel>
              <OutlinedInput
                name="limit_value"
                label="Limit Value"
                id="limit_value"
                type="number"
                value={
                  formik.values.limit_value !== null
                    ? formik.values.limit_value
                    : ""
                }
                onChange={formik.handleChange}
              />
              {formik.touched.limit_value &&
              Boolean(formik.errors.limit_value) ? (
                <FormHelperText id="limit_value-error-text">
                  {formik.errors.limit_value}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={Object.keys(formik.errors).length}
                onClick={() =>
                  !Object.keys(formik.errors).length &&
                  !loading &&
                  !formik.isSubmitting &&
                  formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {isUpdate ? "Update" : "Create"}
              </LoadingButton>
            </Grid>
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                size="large"
                onClick={() => nav("/tmobilej/plan")}
                loading={loading}
                variant="contained"
              >
                Cancel
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default PlanForm;
